import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  REACTIONS,
  resolveMediaInput,
  type MediaControlMessage,
  type MediaHubStateView,
  type MediaItemView,
  type Reaction,
} from '@duck/contracts';
import {
  applyRadioState,
  destroyRadioPlayer,
  ensureRadioPlayer,
  setRadioMuted as applyMuted,
  setRadioVolume as applyVolume,
  unlockRadio,
  type RadioApplyInput,
} from './radio-player';
import { useAudioSettingsStore } from '../../../state/audio-settings.store';
import type { ScreenStatus } from '../../../net/voice-client';

/**
 * The MEDIA HUB card (ADR-0033, emotion: Belonging) — the evolution of the office-radio card into the
 * room's shared screen. It is the ONE visible surface for the zone's host-driven, perfectly-synced
 * player + COLLECTIVE queue + screen share. Everyone in the zone sees the same screen, can add an item
 * to the queue, react, and adjust their OWN volume; only the HOST (the seat holder or the guild OWNER)
 * controls playback, shares a screen, and manages the queue. Playback STATE is the server's; this card
 * reflects it and feeds the {@link import('./radio-player')} reconciler for YouTube sources (the screen
 * share renders a live <video>). Nothing is persisted server-side (Constitution Art. 1); the listener's
 * volume lives in the shared {@link useAudioSettingsStore} `radio` channel (one value for YouTube + the
 * screen audio). Source-agnostic by design: a new kind (Twitch/Vimeo/file…) is a new render branch.
 */

/** sessionStorage key so a reconnect / item change doesn't re-prompt "tap to listen" within a session. */
const UNLOCKED_KEY = 'duck.radio.unlocked';
/** The DOM id the YouTube IFrame replaces — a React-untouched child so React never reconciles it. */
const PLAYER_MOUNT_ID = 'ddo-radio-player';

function readStoredUnlocked(): boolean {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(UNLOCKED_KEY) === '1';
}

export interface MediaHubCardProps {
  /** The server-authoritative hub for the zone the citizen is standing in, or null (idle, no hub yet). */
  hub: MediaHubStateView | null;
  /** Friendly name of the current zone (for "The screen in the Meeting Room"). */
  zoneName: string;
  /** Estimated server-clock offset (ms) for position extrapolation (from the MEDIA_CLOCK probe). */
  clockOffsetMs: number;
  /** My room session id (to tell whether I'm the host + render my own screen self-preview). */
  selfSessionId: string;
  /** Whether I'm the guild OWNER — I may always take the host seat + control playback. */
  isOwner: boolean;
  /** The now-playing SCREEN-SHARE stream (mine or a peer's), when `hub.now.kind === 'screenshare'`. */
  screenStream: MediaStream | null;
  /** My OWN screen-share lifecycle (drives the Share button label + the "● you're live" tell). */
  localScreenStatus: ScreenStatus;
  /** Send a media-hub control message (non-host control is dropped server-side, so always safe to call). */
  onControl: (msg: MediaControlMessage) => void;
  /** Toggle my screen share (opens the browser picker / stops). The hub item follows via the status hook. */
  onToggleShare: () => void;
  /** The latest screen reaction received (SELF + OTHERS), or null. A changing `nonce` floats one emoji. */
  reactionTick: { reaction: string; nonce: number } | null;
  /** Collapsed mode (ADR-0030): a pinned webcam owns the corner — keep the player, drop the bulky chrome. */
  collapsed?: boolean;
}

interface Float {
  id: number;
  emoji: string;
}

/** A short, human label for an item ("Ada · dQw4w9WgXcQ" → title when known). */
function itemLabel(item: MediaItemView): string {
  if (item.title) return item.title;
  if (item.kind === 'screenshare') return `${item.addedByName}'s screen`;
  return item.ref;
}

export function MediaHubCard({
  hub,
  zoneName,
  clockOffsetMs,
  selfSessionId,
  isOwner,
  screenStream,
  localScreenStatus,
  onControl,
  onToggleShare,
  reactionTick,
  collapsed = false,
}: MediaHubCardProps): JSX.Element {
  const reduceMotion = useReducedMotion();
  const mountRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const volume = useAudioSettingsStore((s) => s.radio.volume);
  const muted = useAudioSettingsStore((s) => s.radio.muted);
  const setVolumePref = useAudioSettingsStore((s) => s.setVolume);
  const toggleMutedPref = useAudioSettingsStore((s) => s.toggleMuted);
  const [unlocked, setUnlocked] = useState<boolean>(readStoredUnlocked);
  const [addUrl, setAddUrl] = useState('');
  const [addError, setAddError] = useState('');
  const [note, setNote] = useState('');
  const [floats, setFloats] = useState<Float[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const now = hub?.now ?? null;
  const isYouTube = now?.kind === 'youtube';
  const isScreen = now?.kind === 'screenshare';
  const playing = hub?.playing ?? false;
  const vacant = !hub || hub.hostSessionId === '';
  const isHost = !!hub && (hub.hostSessionId === selfSessionId || isOwner);
  const hostName = hub?.hostName ?? '';

  // The YouTube reconciler only ever drives a TIME-BASED source. For a screen share / idle, the videoId
  // is '' so the IFrame stops (the <video> overlay takes the stage). One reconcile input, kept in a ref.
  const ytVideoId = isYouTube ? now.ref : '';
  const inputRef = useRef<RadioApplyInput>({
    videoId: ytVideoId,
    playing,
    positionMs: hub?.positionMs ?? 0,
    updatedAt: hub?.updatedAt ?? 0,
    epoch: hub?.epoch ?? 0,
    clockOffsetMs,
    volume,
    muted,
    unlocked,
  });

  const isHostRef = useRef(isHost);
  isHostRef.current = isHost;
  const onControlRef = useRef(onControl);
  onControlRef.current = onControl;

  // ── Create the one shared YouTube IFrame on mount; tear it down on unmount. ──
  useEffect(() => {
    const host = mountRef.current;
    if (!host) return;
    const inner = document.createElement('div');
    inner.id = PLAYER_MOUNT_ID;
    inner.style.width = '100%';
    inner.style.height = '100%';
    host.appendChild(inner);

    ensureRadioPlayer(PLAYER_MOUNT_ID, {
      // The host's client advances the queue when its player ends or can't play an item (the server has
      // no media, so it relies on the host's report). Non-hosts just follow the new state.
      onEnded: () => {
        if (isHostRef.current) onControlRef.current({ action: 'next' });
      },
      onError: () => {
        setNote(
          isHostRef.current
            ? "Couldn't play that one — skipping to the next 🎶"
            : "This item won't play here",
        );
        if (isHostRef.current) onControlRef.current({ action: 'next' });
      },
      onPlayingChange: (p) => {
        if (!p) return;
        setUnlocked(true);
        try {
          window.sessionStorage.setItem(UNLOCKED_KEY, '1');
        } catch {
          /* storage unavailable — unlock still holds in state for this session */
        }
      },
    });

    return () => {
      destroyRadioPlayer();
      try {
        host.removeChild(inner);
      } catch {
        /* already gone */
      }
    };
  }, []);

  // ── Reconcile the YouTube player toward the shared state whenever it (or a local pref) changes. ──
  useEffect(() => {
    const input: RadioApplyInput = {
      videoId: ytVideoId,
      playing,
      positionMs: hub?.positionMs ?? 0,
      updatedAt: hub?.updatedAt ?? 0,
      epoch: hub?.epoch ?? 0,
      clockOffsetMs,
      volume,
      muted,
      unlocked,
    };
    inputRef.current = input;
    applyRadioState(input);
  }, [ytVideoId, playing, hub?.positionMs, hub?.updatedAt, hub?.epoch, clockOffsetMs, volume, muted, unlocked]);

  // Gentle drift-correction tick (only seeks past the reconciler's threshold, so it's cheap).
  useEffect(() => {
    const id = setInterval(() => applyRadioState(inputRef.current), 1500);
    return () => clearInterval(id);
  }, []);

  // Wire the screen-share stream into the <video> whenever it changes.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.srcObject !== screenStream) el.srcObject = screenStream;
    if (screenStream) void el.play().catch(() => undefined);
  }, [screenStream, isScreen]);

  // Track fullscreen so the button label/icon stays honest (Esc / the browser can exit it).
  useEffect(() => {
    const onFs = () => setIsFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Clear the transient note when the now-item changes.
  useEffect(() => {
    setNote('');
  }, [now?.id]);

  // ── Float an emoji when a screen reaction arrives (SELF + OTHERS). ──
  const reactionTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    if (!reactionTick) return;
    const float: Float = { id: reactionTick.nonce, emoji: reactionTick.reaction };
    setFloats((prev) => [...prev.slice(-5), float]);
    const t = setTimeout(() => {
      setFloats((prev) => prev.filter((f) => f.id !== float.id));
      reactionTimers.current = reactionTimers.current.filter((x) => x !== t);
    }, 1500);
    reactionTimers.current.push(t);
  }, [reactionTick]);
  useEffect(() => () => reactionTimers.current.forEach(clearTimeout), []);

  const handleUnlock = useCallback(() => {
    unlockRadio();
    setUnlocked(true);
    try {
      window.sessionStorage.setItem(UNLOCKED_KEY, '1');
    } catch {
      /* storage unavailable */
    }
  }, []);

  const handleVolume = useCallback(
    (v: number) => {
      setVolumePref('radio', v);
      applyVolume(v);
    },
    [setVolumePref],
  );

  const handleToggleMuted = useCallback(() => {
    const next = !muted;
    toggleMutedPref('radio');
    applyMuted(next);
  }, [muted, toggleMutedPref]);

  const handleAdd = useCallback(() => {
    const resolved = resolveMediaInput(addUrl);
    if (!resolved) {
      setAddError('Paste a YouTube or YouTube Music link');
      return;
    }
    onControl({ action: 'enqueue', item: { kind: resolved.kind, ref: resolved.ref } });
    setAddUrl('');
    setAddError('');
  }, [addUrl, onControl]);

  const toggleFullscreen = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    if (document.fullscreenElement === stage) void document.exitFullscreen().catch(() => undefined);
    else void stage.requestFullscreen().catch(() => undefined);
  }, []);

  const hasNow = !!now;
  const nowLabel = !hasNow
    ? 'The screen is dark'
    : isScreen
      ? `${now.addedByName} is sharing their screen`
      : playing
        ? `Now playing — added by ${now.addedByName}`
        : 'Paused';

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      aria-label="Media hub"
      className="pointer-events-auto flex max-h-[calc(100vh-1rem)] w-full flex-col gap-2 overflow-y-auto rounded-2xl border border-white/10 bg-black/45 p-3 text-white shadow-2xl backdrop-blur-md"
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          <span aria-hidden="true">🍿</span> Media Hub
          <span className="text-[10px] font-normal text-white/40">· {zoneName}</span>
        </span>
        <div className="flex items-center gap-1.5">
          {isScreen && <span className="text-[10px] uppercase tracking-wide text-rose-300/90">● В эфире</span>}
          {hasNow && !isScreen && (
            <span className={`text-[10px] uppercase tracking-wide ${playing ? 'text-emerald-300/80' : 'text-white/40'}`}>
              {playing ? '● live' : 'paused'}
            </span>
          )}
          {hasNow && (
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              className="rounded px-1 text-xs text-white/60 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            >
              <span aria-hidden="true">{isFullscreen ? '🡼' : '⛶'}</span>
            </button>
          )}
        </div>
      </div>

      {/* The stage: the (ToS-visible) YouTube IFrame, with the live screen <video> overlaid when sharing.
          Fullscreen targets this element so it works for ANY source. Reactions float over the top. */}
      <div ref={stageRef} className="relative aspect-video w-full overflow-hidden rounded-lg bg-black/60">
        <div ref={mountRef} className={`h-full w-full ${isScreen ? 'invisible absolute inset-0' : ''}`} />
        {isScreen && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            // The local host watches their own share muted (they already produce the sound); viewers hear it
            // through the screen-audio element on the media volume.
            muted={now?.ref === selfSessionId}
            className="absolute inset-0 h-full w-full bg-black object-contain"
          />
        )}
        {!hasNow && (
          <div className="absolute inset-0 flex items-center justify-center text-center text-xs text-white/40">
            Nothing on the screen yet — add a link or share your screen
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-1 flex justify-center" aria-hidden="true">
          <AnimatePresence>
            {floats.map((f) => (
              <motion.span
                key={f.id}
                initial={{ opacity: 0, y: 0, scale: 0.8 }}
                animate={{ opacity: 1, y: reduceMotion ? 0 : -28, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduceMotion ? 0.3 : 1.4 }}
                className="px-0.5 text-xl"
              >
                {f.emoji}
              </motion.span>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <p role="status" aria-live="polite" className="truncate text-xs text-white/70">
        🎬 {nowLabel}
        {hostName && <span className="text-white/40"> · host: {hostName}</span>}
      </p>

      {isYouTube && playing && !unlocked && (
        <button
          type="button"
          onClick={handleUnlock}
          className="rounded-xl bg-amber-400 px-3 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        >
          ▶ Tap to watch together
        </button>
      )}

      {note && <p className="text-[11px] text-amber-200/80">{note}</p>}

      {!collapsed && (
        <>
          {/* Per-user LOCAL volume (YouTube + screen audio share the `radio` channel). */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleToggleMuted}
              aria-pressed={muted}
              aria-label={muted ? 'Unmute' : 'Mute'}
              title={muted ? 'Unmute (just for you)' : 'Mute (just for you)'}
              className="rounded-lg px-1.5 py-1 text-base transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            >
              <span aria-hidden="true">{muted || volume <= 0 ? '🔇' : '🔊'}</span>
            </button>
            <input
              type="range"
              min={0}
              max={100}
              value={muted ? 0 : volume}
              aria-label="Media volume"
              onChange={(e) => handleVolume(Number(e.target.value))}
              className="h-1.5 w-full cursor-pointer accent-amber-400"
            />
          </div>

          {/* React to the screen — open to everyone. */}
          <div className="flex flex-wrap gap-1" role="group" aria-label="React to the screen">
            {REACTIONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => onControl({ action: 'react', reaction: r as Reaction })}
                aria-label={`React ${r}`}
                className="rounded-lg px-1.5 py-1 text-base transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
              >
                <span aria-hidden="true">{r}</span>
              </button>
            ))}
          </div>

          {/* Add to the collective queue — ANYONE in the zone may propose an item. */}
          <div className="flex gap-1.5">
            <input
              type="text"
              value={addUrl}
              onChange={(e) => {
                setAddUrl(e.target.value);
                if (addError) setAddError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
              placeholder="Add a YouTube link to the queue…"
              aria-label="Add an item to the queue by link"
              className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-xs text-white placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            />
            <button
              type="button"
              onClick={handleAdd}
              className="rounded-lg bg-white/10 px-2 py-1 text-xs font-semibold transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            >
              Add
            </button>
          </div>
          {addError && <p className="text-[11px] text-amber-200/80">{addError}</p>}

          {/* "Take the decks" — anyone may claim a VACANT host seat (or the OWNER any time). */}
          {vacant && (
            <button
              type="button"
              onClick={() => onControl({ action: 'claimHost' })}
              className="rounded-lg bg-amber-400/90 px-2 py-1 text-xs font-semibold text-slate-900 transition hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            >
              🎧 Take the decks
            </button>
          )}

          {/* HOST controls — the seat holder or the guild OWNER. The server re-checks, so this is a UX gate. */}
          {isHost && (
            <div className="mt-1 flex flex-col gap-2 border-t border-white/10 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-200/70">
                  🎧 You’re hosting
                </span>
                <button
                  type="button"
                  onClick={() => onControl({ action: 'releaseHost' })}
                  className="rounded px-1 text-[10px] text-white/50 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                >
                  Step down
                </button>
              </div>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => onControl({ action: playing ? 'pause' : 'play' })}
                  disabled={!hasNow || isScreen}
                  className="flex-1 rounded-lg bg-white/10 px-2 py-1 text-xs font-semibold transition hover:bg-white/20 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                >
                  {playing ? '⏸ Pause' : '▶ Play'}
                </button>
                <button
                  type="button"
                  onClick={() => onControl({ action: 'next' })}
                  disabled={!hasNow && (hub?.queue.length ?? 0) === 0}
                  className="flex-1 rounded-lg bg-white/10 px-2 py-1 text-xs font-semibold transition hover:bg-white/20 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                >
                  ⏭ Next
                </button>
                <button
                  type="button"
                  onClick={onToggleShare}
                  aria-pressed={localScreenStatus === 'on'}
                  className={`rounded-lg px-2 py-1 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
                    localScreenStatus === 'on' ? 'bg-rose-500/80 hover:bg-rose-500' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {localScreenStatus === 'on' ? '■ Stop' : '🖥 Share'}
                </button>
              </div>

              {(hub?.queue.length ?? 0) > 0 && (
                <ul className="flex flex-col gap-1" aria-label="Up next">
                  {hub!.queue.map((item, i) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-2 py-1 text-[11px]"
                    >
                      <span className="truncate text-white/60">
                        {i + 1}. {itemLabel(item)}
                        <span className="text-white/30"> · {item.addedByName}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => onControl({ action: 'playNow', itemId: item.id })}
                          aria-label={`Play item ${i + 1} now`}
                          className="rounded px-1 text-white/50 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                        >
                          ▶
                        </button>
                        <button
                          type="button"
                          onClick={() => onControl({ action: 'remove', itemId: item.id })}
                          aria-label={`Remove item ${i + 1} from the queue`}
                          className="rounded px-1 text-white/50 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                        >
                          ✕
                        </button>
                      </span>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      onClick={() => onControl({ action: 'clear' })}
                      className="mt-0.5 w-full rounded px-1 py-0.5 text-[10px] text-white/40 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                    >
                      Clear the queue
                    </button>
                  </li>
                </ul>
              )}
            </div>
          )}

          {/* The QUEUE is visible to EVERYONE (read-only for non-hosts) — collective participation. */}
          {!isHost && (hub?.queue.length ?? 0) > 0 && (
            <ul className="flex flex-col gap-1 border-t border-white/10 pt-2" aria-label="Up next">
              {hub!.queue.map((item, i) => (
                <li key={item.id} className="truncate rounded-lg bg-white/5 px-2 py-1 text-[11px] text-white/55">
                  {i + 1}. {itemLabel(item)}
                  <span className="text-white/30"> · {item.addedByName}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </motion.section>
  );
}
